import React, { useContext, useEffect, useState } from 'react';
import { clearCartItem } from "../../Redux/features/Cart/CartSlice";
import { ConfigContext } from '../../Context/ConfigContext';
import axios from 'axios';

const CheckOutPayment = ({ handlePlaceOrder, submiting }) => {
    const { currencyValue, apiURL, apiHeaderJson } = useContext(ConfigContext);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [addressCountry, setAddressCountry] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const [paymentDetails, setPaymentDetails] = useState({
        upiId: '',
        cardNumber: '',
        cardName: '',
        cardExpiry: '',
        cardCvv: ''
    });
    const [errors, setErrors] = useState({});

    const handlePaymentMethodChange = (method) => {
        setPaymentMethod(method);
        setErrors({});
    };

    const validatePaymentDetails = () => {
        const newErrors = {};

        if (paymentMethod === 'upi' && !paymentDetails.upiId) {
            newErrors.upiId = 'UPI ID is required';
        } else if (paymentMethod === 'upi' && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/.test(paymentDetails.upiId)) {
            newErrors.upiId = 'Please enter a valid UPI ID';
        }

        if (paymentMethod === 'card') {
            if (!paymentDetails.cardNumber) {
                newErrors.cardNumber = 'Card number is required';
            } else if (!/^\d{16}$/.test(paymentDetails.cardNumber.replace(/\s/g, ''))) {
                newErrors.cardNumber = 'Please enter a valid 16-digit card number';
            }

            if (!paymentDetails.cardName) {
                newErrors.cardName = 'Name on card is required';
            }

            if (!paymentDetails.cardExpiry) {
                newErrors.cardExpiry = 'Expiry date is required';
            } else if (!/^(0[1-9]|1[0-2])\/?([0-9]{2})$/.test(paymentDetails.cardExpiry)) {
                newErrors.cardExpiry = 'Please use MM/YY format';
            }

            if (!paymentDetails.cardCvv) {
                newErrors.cardCvv = 'CVV is required';
            } else if (!/^\d{3,4}$/.test(paymentDetails.cardCvv)) {
                newErrors.cardCvv = 'Please enter a valid CVV';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCardNumberChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 16) value = value.substring(0, 16);
        value = value.replace(/(\d{4})/g, '$1 ').trim();
        handleInputChange({
            target: {
                name: 'cardNumber',
                value: value
            }
        });
    };

    const handleCardExpiryChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 4) value = value.substring(0, 4);
        if (value.length > 2) {
            value = value.substring(0, 2) + '/' + value.substring(2);
        }
        handleInputChange({
            target: {
                name: 'cardExpiry',
                value: value
            }
        });
    };

    const GetCustomerAddress = async () => {
        const headers = apiHeaderJson;
        try {
            const response = await axios.get(`${apiURL}Customers/GetCustomerAddress?limit=1&page=1`, { headers });

            if (response.data.data && response.data.data.length > 0) {
                const customerAddress = response.data.data[0];
                if (customerAddress.customer_address_id && customerAddress.default_address === 1) {
                    setAddressCountry(customerAddress.country_region);
                    if (customerAddress.country_region.toLowerCase() === 'india') {
                        setPaymentMethod('cod');
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching customer address:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = () => {
        if (!validatePaymentDetails()) {
            return;
        }
        const paymentData = {
            order_payment_method: paymentMethod,
            order_currency: currencyValue ?? "INR"
        };

        handlePlaceOrder(paymentData);
    };

    useEffect(() => {
        GetCustomerAddress();
    }, []);

    if (isLoading) {
        return <div className="text-center py-4">Loading payment options...</div>;
    }

    const isIndia = addressCountry && addressCountry.toLowerCase() === 'india';

    return (
        <>
            <div className="payment ml-30 mt-3">
                <h4 className="mb-30">Payment</h4>
                <div className="payment_option">

                    {/* Cash on Delivery - Only show for India */}
                    {isIndia && (
                        <>
                            <div className="custome-radio">
                                <input
                                    className="form-check-input"
                                    required
                                    type="radio"
                                    name="payment_option"
                                    id="payment_cod"
                                    checked={paymentMethod === 'cod'}
                                    onChange={() => handlePaymentMethodChange('cod')}
                                    data-bs-toggle="collapse"
                                    data-bs-target="#codCollapse"
                                    aria-controls="codCollapse"
                                />
                                <label className="form-check-label" htmlFor="payment_cod">
                                    Cash on Delivery
                                </label>
                            </div>
                            <div className={`collapse ${paymentMethod === 'cod' ? 'show' : ''}`} id="codCollapse">
                                <div className="card card-body">Pay with cash upon delivery.</div>
                            </div>
                        </>
                    )}

                    {/* UPI */}
                    <div className="custome-radio mt-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="payment_option"
                            id="payment_upi"
                            checked={paymentMethod === 'upi'}
                            onChange={() => handlePaymentMethodChange('upi')}
                            data-bs-toggle="collapse"
                            data-bs-target="#upiCollapse"
                            aria-controls="upiCollapse"
                        />
                        <label className="form-check-label" htmlFor="payment_upi">
                            UPI
                        </label>
                    </div>
                    <div className={`collapse ${paymentMethod === 'upi' ? 'show' : ''}`} id="upiCollapse">
                        <div className="card card-body">
                            Enter your UPI ID to proceed.
                            <input
                                type="text"
                                className={`form-control mt-2 ${errors.upiId ? 'is-invalid' : ''}`}
                                placeholder="example@upi"
                                name="upiId"
                                value={paymentDetails.upiId}
                                onChange={handleInputChange}
                            />
                            {errors.upiId && <div className="invalid-feedback">{errors.upiId}</div>}
                        </div>
                    </div>

                    {/* PayPal */}
                    <div className="custome-radio mt-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="payment_option"
                            id="payment_paypal"
                            checked={paymentMethod === 'paypal'}
                            onChange={() => handlePaymentMethodChange('paypal')}
                            data-bs-toggle="collapse"
                            data-bs-target="#paypalCollapse"
                            aria-controls="paypalCollapse"
                        />
                        <label className="form-check-label" htmlFor="payment_paypal">
                            PayPal
                        </label>
                    </div>
                    <div className={`collapse ${paymentMethod === 'paypal' ? 'show' : ''}`} id="paypalCollapse">
                        <div className="card card-body">
                            You will be redirected to PayPal to complete your payment.
                        </div>
                    </div>

                    {/* Credit Card */}
                    <div className="custome-radio mt-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="payment_option"
                            id="payment_card"
                            checked={paymentMethod === 'card'}
                            onChange={() => handlePaymentMethodChange('card')}
                            data-bs-toggle="collapse"
                            data-bs-target="#cardCollapse"
                            aria-controls="cardCollapse"
                        />
                        <label className="form-check-label" htmlFor="payment_card">
                            Credit/Debit Card
                        </label>
                    </div>
                    <div className={`collapse ${paymentMethod === 'card' ? 'show' : ''}`} id="cardCollapse">
                        <div className="card card-body">
                            <input
                                type="text"
                                className={`form-control mb-2 ${errors.cardNumber ? 'is-invalid' : ''}`}
                                placeholder="Card Number"
                                name="cardNumber"
                                value={paymentDetails.cardNumber}
                                onChange={handleCardNumberChange}
                            />
                            {errors.cardNumber && <div className="invalid-feedback">{errors.cardNumber}</div>}

                            <input
                                type="text"
                                className={`form-control mb-2 ${errors.cardName ? 'is-invalid' : ''}`}
                                placeholder="Name on Card"
                                name="cardName"
                                value={paymentDetails.cardName}
                                onChange={handleInputChange}
                            />
                            {errors.cardName && <div className="invalid-feedback">{errors.cardName}</div>}

                            <div className="row">
                                <div className="col-md-6">
                                    <input
                                        type="text"
                                        className={`form-control mb-2 ${errors.cardExpiry ? 'is-invalid' : ''}`}
                                        placeholder="Expiry Date (MM/YY)"
                                        name="cardExpiry"
                                        value={paymentDetails.cardExpiry}
                                        onChange={handleCardExpiryChange}
                                    />
                                    {errors.cardExpiry && <div className="invalid-feedback">{errors.cardExpiry}</div>}
                                </div>
                                <div className="col-md-6">
                                    <input
                                        type="password"
                                        className={`form-control mb-2 ${errors.cardCvv ? 'is-invalid' : ''}`}
                                        placeholder="CVV"
                                        name="cardCvv"
                                        value={paymentDetails.cardCvv}
                                        onChange={handleInputChange}
                                        maxLength="4"
                                    />
                                    {errors.cardCvv && <div className="invalid-feedback">{errors.cardCvv}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <img src="/assets/imgs/theme/payment-gateways.png" width={"40%"} alt="Payment methods" />
                <div className='text-center mb-3'>
                    <button
                        className="btn btn-fill-out btn-block mt-30"
                        onClick={handleSubmit}
                        disabled={submiting}
                    >
                        {submiting ? 'Placing.....' : 'Place an Order'}
                        {!submiting && <i className="fi-rs-sign-out ml-15" />}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CheckOutPayment;